/**
 * The auth-record props whose values are structured (not scalars) and therefore
 * have to be JSON-encoded on the way into redis and parsed on the way out.
 *
 * This is the SINGLE SOURCE OF TRUTH — `GateServer`'s `getUserAuth`,
 * `setUserAuth` and `updateUserAuth`, and `RedisGateStateStore`, all derive
 * their prop lists from `ENCODED_AUTH_PROP_KEYS` below rather than repeating
 * a literal. Adding a structured field to `AuthenticatedGateClient` means
 * adding one key here; forgetting it used to mean a silent redis-only
 * data-loss bug that no unit test in this repo covered (there was no
 * redis-backed gate fixture) before the `GateStateStore` conformance suite.
 *
 * Lives here — not in `GateServer.ts`, where it originated — so
 * `RedisGateStateStore` can import it without creating a
 * `GateServer -> state -> GateServer` import cycle now that `GateServer` is
 * headed toward routing through the store (Task 3). `GateServer.ts`
 * re-exports both names for backward compatibility with existing importers.
 *
 * `areas` USED to be listed here — the client→area index was an array field on
 * the auth record, JSON-encoded into the record blob on every report. It moved
 * out to the store's own per-area index (`addClientArea`/`removeClientArea`/
 * `getClientAreas`/`clearClientAreas`), so the encode/decode path this list
 * describes no longer touches it at all: one atomic row per area instead of a
 * whole-list read-modify-write of an encoded blob.
 */
export const ENCODABLE_AUTH_PROPS = {
    auth: true,
    givenGameList: true,
}

export const ENCODED_AUTH_PROP_KEYS = Object.keys(ENCODABLE_AUTH_PROPS);

/**
 * The auth-record props declared `string | number`, and the companion hash
 * field a redis-backed store writes ALONGSIDE each one to record the JS type it
 * was written with, so the record round-trips with that type instead of with
 * the string a redis hash can store.
 *
 * ONE mechanism, deliberately, for every such prop. `publicId` came first and
 * `gameId` was added second, and the second one is written, restored and
 * stripped by the same three helpers below rather than by a parallel copy of
 * them — a marker that a store forgot to write is invisible until a game trips
 * over it in production, so there must be exactly one place that can forget.
 *
 * ── WHY THE TYPE IS PART OF THE CONTRACT ────────────────────────────────────
 * `publicId` is `string | number`: `useSequentialPublicIds` mints NUMBERS, the
 * default scheme mints 5-character strings. Everything a game keys a player by
 * is this value, and governing rule 1 of the redis design promises no
 * game-visible difference between backings — but a redis hash stores only
 * strings, so a numeric id written as `1` came back as `"1"`. That broke two
 * things at once: `ConnectorServer`'s seat validation rejected every handshake
 * (see `registerClientValidation`), and — once that was fixed — game code
 * comparing `publicId === 1` silently stopped matching the moment
 * `servers.redis` was configured. The type is not cosmetic; it is the contract.
 *
 * `gameId` is `GameId = string | number` for the same reason —
 * `Master.addGame(gameId, gameType)` accepts either — and it was written off
 * once as an internal-only "representation residual" on the grounds that every
 * comparison the GATE makes against it is already `String()`-normalized
 * (`rejoinAreaIdsFor`, `identityPathFor`). That reasoning only ever covered the
 * gate's own reads. The record does not stop at the gate: `getUserAuth` hands
 * it STRAIGHT to `onJoinGame(client, data)`, to `onRemovedAuth(auth)`, and to
 * every caller of the public `getClientByPublicId`. A game whose `onJoinGame`
 * returns the game the player is already in — `return client.gameId`, the shape
 * the integration harness's own `TestGateServer` uses — feeds that value back
 * into `handleJoinGame`'s STRICT `this.games.find(g => g.id === gameId)`, which
 * resolves in memory mode and throws `Did not find game for gameId: 42` the
 * moment `servers.redis` is set. Same defect, same fix, one layer out.
 *
 * ── WHY NOT `ENCODED_AUTH_PROP_KEYS` ───────────────────────────────────────
 * The obvious lever is to add these to the list above and let
 * `stringifyProps`/`tryParseProps` JSON round-trip them the way they do `auth`.
 * IT DOES NOT WORK, and it fails LOUDLY on the DEFAULT configuration: those two
 * helpers are asymmetric. `stringifyProps` only encodes values that are already
 * `typeof === "object"` (a scalar is left alone, so a number is never quoted),
 * while `tryParseProps` `JSON.parse`s ANY string it finds under a listed key,
 * with no try/catch. A numeric id happens to survive (`JSON.parse("1")` is 1),
 * but the default scheme's `"qFHg6O8c"` throws `SyntaxError: Unexpected token
 * 'q'` — i.e. adding the key would turn "sequential ids don't work on redis"
 * into "NOTHING works on redis". Verified before this was written.
 *
 * ── WHY A COMPANION FIELD RATHER THAN ENCODING THE VALUE ITSELF ────────────
 * Encoding the value (`JSON.stringify` on write, tolerant parse on read) would
 * also work, but it CHANGES THE STORED FORM OF AN EXISTING FIELD: `publicId`
 * would go from `qFHg6O8c` to `"qFHg6O8c"` in the hash. The redis-backed gate
 * predates this store (`RedisGateStateStore` ports GateServer's original
 * `if (this.redis)` forks under the SAME `p:{privateId}` / `pubIdMap` keys), so
 * records in that old form can already exist, and the default string-id scheme
 * on redis WORKS today — a rolling upgrade in which one gate writes quoted ids
 * and another reads them raw would break a configuration that is currently
 * fine. A companion field is compatible in BOTH directions: an old record
 * simply has no marker and reads back exactly as it does today (a string, which
 * is the only type the working scheme ever had), and an old gate reading a new
 * record sees the described field byte-for-byte unchanged and ignores the extra
 * one. The one combination whose stored form is genuinely new — sequential ids
 * on redis — could never seat a client at all, so it has no deployed records to
 * be compatible with.
 *
 * It also cannot go stale: `writeTypeMarkers` is written UNCONDITIONALLY beside
 * every write of a marked prop (never "only when numeric"), so a marker can
 * never survive a change of type, and no compensating delete is needed.
 */
export const TYPE_MARKED_AUTH_PROPS: Readonly<Record<string, string>> = {
    publicId: 'publicIdType',
    gameId: 'gameIdType',
};

/** The companion field names, i.e. what `restoreTypedAuthProps` strips. */
export const TYPE_MARKER_FIELDS = Object.values(TYPE_MARKED_AUTH_PROPS);

/**
 * The marker field for a record key, or `undefined` if that key carries no
 * type. `updateAuthField` — the field's real primary writer on the live join
 * path (`reserveSeat` persists the game with
 * `updateUserAuth(privateId, 'gameId', gameId)`) — dispatches on this, so a
 * prop added to the map above is marked by BOTH writers with no further edit.
 */
export function typeMarkerFieldFor(key: string): string | undefined {
    return TYPE_MARKED_AUTH_PROPS[key];
}

/** The marker value to store beside a marked prop. */
export function typeMarkerValue(value: unknown): string {
    return typeof value;
}

/**
 * Stamp the marker for every marked prop PRESENT in an outbound hash.
 *
 * Keyed off `toWrite` rather than off `record`, deliberately: the caller has
 * already dropped `undefined`-valued fields (the C1 guard), and a marker
 * describing a field that was not written would be a lie the next reader acts
 * on. Written for every present prop regardless of its type — see the "cannot
 * go stale" note above.
 */
export function writeTypeMarkers(toWrite: Record<string, any>, record: any): void {
    for(const prop of Object.keys(TYPE_MARKED_AUTH_PROPS)) {
        if(prop in toWrite) toWrite[TYPE_MARKED_AUTH_PROPS[prop]] = typeMarkerValue(record[prop]);
    }
}

/**
 * Restore every marked prop's original type on a record read out of a
 * string-valued backing, and REMOVE the marker fields — the gate must get back
 * exactly the record shape it wrote (it re-persists this very object, and a
 * memory-backed store has no markers to hand back).
 *
 * Only `'number'` is restored. `'string'` needs no work, and no other type is
 * representable in `publicId`/`gameId`. A marker that is absent (an old record,
 * or a record written without that prop) leaves the value untouched — the
 * conservative direction: an all-digits STRING id must NOT be turned into a
 * number, and both `makeId(5)` and `Master.addGame('42', ...)` really can
 * produce one.
 */
export function restoreTypedAuthProps(record: any): any {
    if(!record || typeof record !== 'object') return record;
    for(const prop of Object.keys(TYPE_MARKED_AUTH_PROPS)) {
        const markerField = TYPE_MARKED_AUTH_PROPS[prop];
        const marker = record[markerField];
        delete record[markerField];
        if(marker !== 'number') continue;
        const value = record[prop];
        if(typeof value !== 'string' || value === '') continue;
        const asNumber = Number(value);
        // Guarded, not blind: a marker that disagrees with the stored value
        // (corruption, a hand-edited key) must leave the value alone rather than
        // write `NaN` into the id every seat and entity is keyed by.
        if(Number.isFinite(asNumber)) record[prop] = asNumber;
    }
    return record;
}

/**
 * The auth-record props declared as PLAIN `number` on `AuthenticatedGateClient`,
 * restored to that type on the way out of a string-valued backing.
 *
 * ── WHY THIS EXISTS, SEPARATELY FROM THE TYPE MARKERS ──────────────────────
 * `publicId`/`gameId` need companion type markers because they are genuinely
 * `string | number` — a hash cannot tell `1` from `"1"`, and turning an
 * all-digits STRING id into a number would be the same rule break in the other
 * direction. These fields have no such ambiguity: their declared type admits
 * only `number`, so the restore is unconditional and needs nothing stored
 * beside them.
 *
 * FOUND BY THE REDIS PARITY RUN of the shared integration harness — the nine
 * `createMinEnv`/`createMinEnvLocal` suites run a second time with
 * `GOTTI_TEST_REDIS_URL` set. The gate's auth record, printed on both backings:
 *
 *   memory: {"time":1786049567295,...}
 *   redis:  {"time":"1786049561725",...}
 *
 * `time` is not internal bookkeeping — `getUserAuth` hands that record STRAIGHT
 * to `onJoinGame(client, data)`, so it is game-visible, and governing rule 1
 * says a game must not be able to tell the backings apart. It had neither a
 * marker nor a defence.
 *
 * `heldGraceMs` is the same defect one layer quieter. Every INTERNAL read of it
 * is already wrapped in `Number(...)` (`graceHold`, `consumeRejoinAreas`, the
 * area-report reconcile) — which is what kept a string from breaking the gate —
 * but `resumeSession` copies the raw value onto the fresh record, so the string
 * propagated anyway. Fixing the round trip is the right layer: the consumers
 * should not each have to remember which backing they are on.
 *
 * ── ORDER-INDEPENDENT, BY CONSTRUCTION ─────────────────────────────────────
 * `getAuthRecord` runs `restoreTypedAuthProps` and `restoreNumericAuthProps`
 * back to back. They touch DISJOINT fields — `publicId`/`gameId` (plus their
 * marker fields, which only the first one knows about) versus
 * `time`/`heldGraceMs` — and neither reads anything the other writes, so
 * swapping them changes nothing. Pinned by the conformance suite's "all three
 * type restores compose on ONE record" case, which drives a record carrying
 * every one of those fields at once.
 */
export const NUMERIC_AUTH_PROPS = ['time', 'heldGraceMs'];

/**
 * Restore the declared-numeric props on a record read out of a string-valued
 * backing. Absent props are left absent (they are skipped on write, never
 * written as `undefined`), and a value that does not parse finitely is left
 * ALONE rather than replaced with `NaN` — the same conservative direction
 * `restoreTypedAuthProps` takes for a marker that disagrees with its value.
 */
export function restoreNumericAuthProps(record: any): any {
    if(!record || typeof record !== 'object') return record;
    for(const prop of NUMERIC_AUTH_PROPS) {
        const value = record[prop];
        if(typeof value !== 'string' || value === '') continue;
        const asNumber = Number(value);
        if(Number.isFinite(asNumber)) record[prop] = asNumber;
    }
    return record;
}

/**
 * The auth-record props NORMALIZED TO THEIR STRING FORM ON WRITE, on every
 * backing — so the stored value matches the type the field is declared with and
 * there is nothing left to restore on the way out.
 *
 * ── WHY NORMALIZE RATHER THAN MARK THE TYPE ────────────────────────────────
 * `connectorId` looks like the same defect as `publicId`/`gameId` one field
 * over — `reserveSeat` writes `connector.id`, `RegisteredServerData.id` is
 * `string | number`, connector ids really can be numeric and really can be `0`
 * — and the record does not stop at the gate: `getUserAuth` hands it STRAIGHT
 * to `onJoinGame(client, data)` and to `onRemovedAuth(auth)`. So a numeric
 * connector id was game-visible as `0` in memory mode and `"0"` on a redis
 * gate, which is the governing-rule-1 break the markers exist to prevent.
 *
 * It does NOT get a marker, because it is not the same KIND of field:
 *
 *  - `publicId` and `gameId` are genuinely `string | number` and GAME-FACING.
 *    A game picks its own game ids (`Master.addGame` accepts either) and
 *    `useSequentialPublicIds` mints NUMBERS, so their type is part of the
 *    public contract; a marker preserves a real, chosen distinction and
 *    converting them to always-string would be a breaking API change.
 *  - `connectorId` is internal plumbing. No game chooses it, no public API
 *    exposes it, and `AuthenticatedGateClient` ALREADY DECLARES IT
 *    `connectorId?: string`. A marker would have faithfully preserved an
 *    ambiguity that should never have existed; normalizing on write makes the
 *    value honour the declaration and removes the ambiguity outright — a
 *    smaller fix that deletes the failure class instead of encoding it.
 *
 * It is also what the store ALREADY does with every OTHER connector id it
 * keeps: `setGraceHold` and `setReservingSeat` both `String()` on write on both
 * backings, and the connector→clients index is `String()`-keyed. The record was
 * the one place that did not, which is why every gate-side read of it had to be
 * written `String(auth.connectorId) !== String(other)` by hand
 * (`applyClientAreaChange`, `handleConnectorClosed`, `reserveSeat`'s re-seat
 * check). Those comparisons stay correct either way — normalizing just stops
 * them from being load-bearing.
 *
 * ── WHY WRITE-SIDE, NOT READ-SIDE ──────────────────────────────────────────
 * A read-side coercion would leave the two backings storing DIFFERENT bytes for
 * the same record, and the memory store hands back the caller's own live object
 * (it wraps `GateServer.authenticatedClients`), so a read-side fix would have
 * to mutate on every read or hand back a copy. Normalizing on write makes the
 * stored form identical on both backings, so `getAuthRecord` needs no
 * connectorId step at all and there is nothing for a future backing to forget.
 *
 * ROUTING IS UNAFFECTED. `sendToServer`/`requestServer` look the id up in
 * `localServers`/`serverConnections`, which are PLAIN OBJECTS — every property
 * key is a string in JS, so `[0]` and `['0']` are the same key. (The gate's one
 * connector-keyed `Set`, `drainingConnectors`, is keyed off the connector's OWN
 * `v.id` and never off the auth record.) Pinned by
 * `GateServer.connectorIdZero.test.ts`.
 */
export const STRING_NORMALIZED_AUTH_PROPS = ['connectorId'];

/** Whether `key` is one of the props normalized to its string form on write. */
export function isStringNormalizedAuthProp(key: string): boolean {
    return STRING_NORMALIZED_AUTH_PROPS.includes(key);
}

/**
 * The value to STORE for `key` — its string form for a normalized prop, and the
 * value untouched for everything else. The single-field half of
 * `normalizeStringAuthProps`, for `updateAuthField` (the live join path's real
 * writer: `reserveSeat` persists the granted seat with
 * `updateUserAuth(privateId, 'connectorId', connector.id)` and never touches
 * `setAuthRecord`).
 *
 * `null`/`undefined` pass through UNCHANGED rather than becoming the strings
 * `"null"`/`"undefined"` — an absent connector must stay absent, since that is
 * exactly what every `!= null` guard on this field tests for.
 */
export function normalizeStringAuthValue(key: string, value: any): any {
    if(!isStringNormalizedAuthProp(key)) return value;
    if(value === undefined || value === null) return value;
    return String(value);
}

/**
 * Normalize every string-normalized prop PRESENT on an outbound record, in
 * place. Mutates rather than copying because `MemoryGateStateStore` stores the
 * caller's own object — the gate and the store must not end up looking at the
 * same record and disagreeing about the field's type.
 */
export function normalizeStringAuthProps(record: any): any {
    if(!record || typeof record !== 'object') return record;
    for(const prop of STRING_NORMALIZED_AUTH_PROPS) {
        if(!(prop in record)) continue;
        record[prop] = normalizeStringAuthValue(prop, record[prop]);
    }
    return record;
}
